import { Link, useLocation } from "react-router-dom";
import { Sun, MapPin, Phone, Mail } from "lucide-react";
import { useSettings, AddressLines } from "../lib/settings";
import { fileUrl } from "../lib/api";
import { MobileMenu } from "./MobileMenu";

function Brand({ withTagline = true }) {
  const { settings } = useSettings();
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden shrink-0">
        {settings.logo_url ? (
          <img src={fileUrl(settings.logo_url)} alt={settings.company_name} className="w-full h-full object-cover" />
        ) : (
          <Sun className="w-6 h-6 text-primary" strokeWidth={1.75} />
        )}
      </div>
      <div className="leading-tight min-w-0">
        <div className="font-heading text-lg sm:text-xl font-bold tracking-tight truncate" data-testid="brand-name">
          {settings.company_name}
        </div>
        {withTagline && (
          <div className="text-[11px] sm:text-xs text-muted-foreground tracking-wide truncate">{settings.tagline}</div>
        )}
      </div>
    </div>
  );
}

export function Header() {
  const location = useLocation();
  const nav = [
    { to: "/", label: "Home" },
    { to: "/products", label: "Products" },
    { to: "/contact", label: "Contact" },
    { to: "/admin", label: "Admin" },
  ];
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-white/40 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-3">
        <Link to="/" data-testid="brand-link" className="min-w-0">
          <Brand />
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors ${
                location.pathname === n.to
                  ? "text-primary"
                  : "text-secondary hover:text-primary"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <Link
          to="/contact"
          data-testid="header-quote-btn"
          className="hidden md:inline-flex bg-primary text-white hover:bg-orange-600 transition-colors font-medium rounded-full px-6 py-2.5 text-sm"
        >
          Get Free Quote
        </Link>
        <MobileMenu nav={nav} />
      </div>
    </header>
  );
}

export function Footer() {
  const { settings } = useSettings();
  return (
    <footer data-testid="site-footer" className="bg-secondary text-white mt-16 sm:mt-24 pb-16 md:pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center overflow-hidden">
              {settings.logo_url ? (
                <img src={fileUrl(settings.logo_url)} alt={settings.company_name} className="w-full h-full object-cover" />
              ) : (
                <Sun className="w-6 h-6 text-white" strokeWidth={1.75} />
              )}
            </div>
            <div className="font-heading text-2xl font-bold">{settings.company_name}</div>
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            Renewable Energy Solution Provider in Akola. Panels, inverters, batteries and complete rooftop installations.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">Visit Our Shop</div>
          <div className="flex items-start gap-3 text-white/90">
            <MapPin className="w-5 h-5 mt-0.5 text-primary" strokeWidth={1.75} />
            <p className="text-sm leading-relaxed" data-testid="footer-address">
              <AddressLines text={settings.shop_address} />
            </p>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">Get in Touch</div>
          <div className="space-y-3 text-sm">
            {settings.shop_phone && (
              <a href={`tel:${settings.shop_phone.replace(/[^\d+]/g, "")}`} className="flex items-center gap-3 hover:text-primary" data-testid="footer-phone">
                <Phone className="w-4 h-4 text-primary" strokeWidth={1.75} />
                <span>{settings.shop_phone}</span>
              </a>
            )}
            {settings.shop_email && (
              <a href={`mailto:${settings.shop_email}`} className="flex items-center gap-3 hover:text-primary" data-testid="footer-email">
                <Mail className="w-4 h-4 text-primary" strokeWidth={1.75} />
                <span>{settings.shop_email}</span>
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/50">
        © {new Date().getFullYear()} {settings.company_name}. All rights reserved.
      </div>
    </footer>
  );
}
